<template>
  <!-- Delete Confirmation Modal -->
  <div class="modal fade" id="deleteSupplierModal" ref="deleteSupplierModal" tabindex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="true">
    <div class="modal-dialog" role="document">
      <div class="modal-content" v-if="supplierToDelete">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('page.supplier.deleteConfirmTitle') }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>{{ $t('page.supplier.deleteConfirmMessage', { supplierName: supplierToDelete.name, supplierCode: supplierToDelete.code }) }}</p>
          <div class="card">
            <div class="card-body">
              <h6 class="card-title">{{ supplierToDelete.code }}</h6>
              <p class="card-text">{{ supplierToDelete.name }}</p>
              <small class="text-muted">{{ $t('field.generic.linesCount') }}: {{ supplierToDelete.supplierItems.length }}</small>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-danger" @click="confirmDeleteSupplier">
            <font-awesome-icon :icon="['fas', 'trash']" />
            {{ $t('action.delete') }}
          </button>
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
            <font-awesome-icon :icon="['fas', 'xmark']" />
            {{ $t('action.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="container-fluid p-0">
    <table v-if="sortedSuppliers.length > 0" class="table table-hover table-sm table-fit align-middle">
      <thead>
        <tr>
          <th>{{ $t('field.generic.supplier') }}</th>
          <th class="d-none d-lg-table-cell">{{ $t('field.generic.linesCount') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="supplier in sortedSuppliers" :key="supplier.id">
          <td style="cursor: pointer" @click="emit('selected', supplier.id)">
            <button class="btn btn-link">
              {{ supplier.code }}
            </button>
            <span class="fw-bold text-primary align-middle">
              {{ supplier.name }}
            </span>
          </td>
          <td class="d-none d-lg-table-cell text-end"><span class="badge text-bg-secondary" :class="{ 'text-bg-info': supplier.supplierItems.length === 0 }">{{ supplier.supplierItems.length }}</span></td>
          <td>
            <button class="btn btn-outline-danger" type="button" @click="showDeleteConfirmation(supplier)">
              <font-awesome-icon :icon="['fas', 'trash']" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { toast } from 'vue3-toastify';
import { Modal } from 'bootstrap';
import { useSessionStore, useSupplierItemsStore, useSuppliersStore } from '@/stores';
import SupplierWithItems from '@/models/frontend/SupplierWithItems';

const sessionStore = useSessionStore();
const suppliersStore = useSuppliersStore();
const supplierItemsStore = useSupplierItemsStore();

const { selectedSiteId } = storeToRefs(sessionStore);
const { data: suppliers } = storeToRefs(suppliersStore);
const { data: supplierItems } = storeToRefs(supplierItemsStore);

const deleteSupplierModal = ref();
const supplierToDelete = ref<SupplierWithItems | null>(null);

const emit = defineEmits<{
  (e: 'selected', id: number): void
}>();

const sortedSuppliers = computed(() => {
  const suppliersWithItems = [...suppliers.value].map((supplier) => {
    const items = supplierItems.value.filter((item) => item.supplier.id === supplier.id);
    return new SupplierWithItems(
      supplier.id,
      supplier.tenant,
      supplier.code,
      supplier.name,
      supplier.email,
      supplier.customerCode,
      supplier.info,
      [...items]);
  });
  return [...suppliersWithItems].sort((a, b) => a.name.localeCompare(b.name));
});

const showDeleteConfirmation = (supplier: SupplierWithItems) => {
  supplierToDelete.value = supplier;
  const modalElement = deleteSupplierModal.value as HTMLElement;
  const modal = new Modal(modalElement);
  modal.show();
};

const hideModal = () => {
  const modalElement = deleteSupplierModal.value as HTMLElement;
  const modal = Modal.getInstance(modalElement);
  modal?.hide();
  supplierToDelete.value = null;
};

const confirmDeleteSupplier = async () => {
  if (supplierToDelete.value) {
    await suppliersStore.delete(supplierToDelete.value.id);
    hideModal();
  }
};

onMounted(async () => {
  // await suppliersStore.fetchAll();
  if (selectedSiteId.value === 0) {
    toast.error('No site selected', { autoClose: 10000 });
  }
});
</script>
